import React, { useState } from 'react';
import { FaChartLine, FaBoxes, FaShoppingCart, FaCalculator, FaComments, FaStar } from 'react-icons/fa';

const Step5Tour = ({ onNext, onBack, plan }) => {
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: <FaChartLine className="text-5xl text-blue-500" />,
      title: 'Dashboard',
      description: 'View your sales, orders, and key metrics at a glance. Real-time updates keep you informed.',
      available: true
    },
    {
      icon: <FaBoxes className="text-5xl text-green-500" />,
      title: 'Products',
      description: 'Manage your product catalog, track inventory, and monitor performance.',
      available: true
    },
    {
      icon: <FaShoppingCart className="text-5xl text-purple-500" />,
      title: 'Orders',
      description: 'Track all your orders, filter by status, and export data for analysis.',
      available: true
    },
    {
      icon: <FaCalculator className="text-5xl text-orange-500" />,
      title: 'AI Calculator',
      description: 'Get AI-powered pricing recommendations with 4 different strategies.',
      available: true
    },
    {
      icon: <FaComments className="text-5xl text-indigo-500" />,
      title: 'AI Chat',
      description: 'Ask questions about your business and get intelligent answers with web search.',
      available: true
    },
    {
      icon: <FaStar className="text-5xl text-yellow-500" />,
      title: 'Pro Features',
      description: 'Profit Tracker, Predictive Alerts, Money Recovery, and Amazon Ads management.',
      available: plan === 'pro'
    }
  ];

  const handleNext = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature(currentFeature + 1);
    } else {
      onNext({ tourCompleted: true });
    }
  };

  const handlePrevious = () => {
    if (currentFeature > 0) {
      setCurrentFeature(currentFeature - 1);
    }
  };

  const feature = features[currentFeature];

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Explore Genia+ Features
        </h2>
        <p className="text-gray-600">
          Let's take a quick tour of what you can do
        </p>
        <div className="mt-4 flex justify-center space-x-2">
          {features.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-12 rounded-full transition-all duration-200 ${
                index === currentFeature
                  ? 'bg-blue-600'
                  : index < currentFeature
                  ? 'bg-blue-300'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="text-center py-12">
        <div className="flex justify-center mb-6">
          {feature.icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
          {feature.description}
        </p>
        
        {!feature.available && (
          <div className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg">
            <p className="text-sm font-medium">
              Upgrade to Pro to unlock this feature
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-between items-center">
        <button
          onClick={currentFeature === 0 ? onBack : handlePrevious}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          ← {currentFeature === 0 ? 'Back' : 'Previous'}
        </button>
        
        <div className="text-sm text-gray-500">
          {currentFeature + 1} of {features.length}
        </div>

        <button
          onClick={handleNext}
          className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
        >
          {currentFeature === features.length - 1 ? 'Finish Tour' : 'Next'} →
        </button>
      </div>

      {currentFeature === features.length - 1 && (
        <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg text-center">
          <h4 className="text-xl font-bold text-gray-900 mb-2">
            🎉 You're All Set!
          </h4>
          <p className="text-gray-700 mb-4">
            Your account is configured and ready to use. Start exploring Genia+ and grow your Amazon business!
          </p>
          <button
            onClick={() => onNext({ tourCompleted: true })}
            className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg"
          >
            Go to Dashboard →
          </button>
        </div>
      )}
    </div>
  );
};

export default Step5Tour;
